import React from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getAllSalesUsers, updateLeadById } from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const UpdateSalesUser = ({ onClose, leadId, currentSalesId, onSuccess, projectId }) => {
  const userId = Cookies.get('userId');
  const { register, handleSubmit } = useForm({
    defaultValues: {
      sales_id: currentSalesId?.toString() || ''
    }
  });

  // Fetch all sales users (includes level, manager/reportees for each user)
  const { data: salesUsers } = useQuery({
    queryKey: ['salesUsers'],
    queryFn: () => getAllSalesUsers()
  });

  // When current user is Sales Manager (level Medium): show only self + sales executives for this project (reportees from manager array filtered by project_id)
  const assignableUsers = React.useMemo(() => {
    const list = salesUsers?.data || [];
    const currentUser = list.find((u) => u.id === parseInt(userId, 10));
    const isSalesManager = currentUser?.level === 'Medium';

    if (isSalesManager && currentUser) {
      const projectIdNum = projectId != null && projectId !== '' ? parseInt(projectId, 10) : null;
      const managerEntries = currentUser.manager || [];
      const forProject = projectIdNum != null
        ? managerEntries.filter((m) => m.project_id === projectIdNum)
        : managerEntries;
      const reportees = forProject.map((m) => m.user).filter(Boolean);
      const uniqueReportees = reportees.filter(
        (u, i, arr) => arr.findIndex((x) => x.id === u.id) === i
      );
      const optionsList = [
        currentUser,
        ...uniqueReportees.filter((u) => u.is_active)
      ];
      return optionsList;
    }

    return list.filter((user) => user.is_active);
  }, [salesUsers?.data, userId, projectId]);

  // Mutation for updating lead
  const { mutate: updateLead, isLoading } = useMutation({
    mutationFn: (data) => updateLeadById(leadId, data),
    onSuccess: () => {
      toast.success('Sales user updated successfully');
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to update sales user'
      );
    }
  });

  const onSubmit = (data) => {
    updateLead({ sales_id: parseInt(data.sales_id) });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <FormInput
        width={100}
        label={'Assign To Sales User'}
        register={register}
        register_key={'sales_id'}
        type={'dropdown'}
        options={[
          { value: '', label: 'Select Sales User', disabled: true },
          ...assignableUsers.map((user) => ({
            value: user.id.toString(),
            label: user.name
          }))
        ]}
      />

      <div className="flex justify-end items-center space-x-4 mt-4">
        <Button type="button" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          Update
        </Button>
      </div>
    </form>
  );
};

export default UpdateSalesUser;
